import { createSignal, Show, For, onCleanup, onMount } from "solid-js";
import { Portal } from "solid-js/web";
import { useTranslation } from "../hooks/useTranslation";
import { parseQuantityExpression } from "./QuantityInput";
import { quantityBreakdown } from "../services/quantityFormatter";
import { formatInteger } from "../services/numberFormat";
import styles from "./Dialog.module.css";

export interface DialogField {
  key: string;
  label: string;
  type?: "text" | "number" | "select" | "date";
  defaultValue?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  // Choices for a select field. Unlike presets, picking one does not submit the dialog, so a
  // select can be combined with other fields.
  options?: { label: string; value: string }[];
  hint?: string;
  presets?: { label: string; value: string }[];
  quantityParse?: boolean;
}

interface DialogState {
  title: string;
  description?: string;
  fields: DialogField[];
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  resolve: (result: Record<string, string> | null) => void;
}

const [dialogState, setDialogState] = createSignal<DialogState | null>(null);

export function showDialog(opts: {
  title: string;
  description?: string;
  fields?: DialogField[];
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}): Promise<Record<string, string> | null> {
  return new Promise((resolve) => {
    setDialogState({
      title: opts.title,
      description: opts.description,
      fields: opts.fields ?? [],
      // Left undefined so DialogHost can translate the defaults; showDialog runs outside a
      // component, where the translation hook is not available.
      confirmLabel: opts.confirmLabel,
      cancelLabel: opts.cancelLabel,
      danger: opts.danger,
      resolve,
    });
  });
}

export function showConfirm(title: string, danger = false, opts?: { confirmLabel?: string; cancelLabel?: string }): Promise<boolean> {
  return showDialog({
    title,
    danger,
    confirmLabel: opts?.confirmLabel,
    cancelLabel: opts?.cancelLabel,
  }).then((r) => r !== null);
}

export function showPrompt(
  title: string,
  fieldLabel: string,
  defaultValue = "",
  opts?: { type?: "text" | "number"; min?: number; max?: number; placeholder?: string; presets?: { label: string; value: string }[] }
): Promise<string | null> {
  return showDialog({
    title,
    fields: [
      {
        key: "value",
        label: fieldLabel,
        defaultValue,
        type: opts?.type ?? "text",
        min: opts?.min,
        max: opts?.max,
        placeholder: opts?.placeholder,
        presets: opts?.presets,
      },
    ],
  }).then((r) => r?.value ?? null);
}

export function DialogHost() {
  const { t } = useTranslation();

  return (
    <Show when={dialogState()}>
      {(state) => {
        const initV: Record<string, string> = {};
        for (const f of state().fields) initV[f.key] = f.defaultValue ?? "";
        const [values, setValues] = createSignal<Record<string, string>>(initV);

        const initValues: Record<string, string> = {};
        for (const f of state().fields) initValues[f.key] = f.defaultValue ?? "";
        setValues(initValues);

        let firstInput: HTMLInputElement | HTMLSelectElement | undefined;
        let dialogRef: HTMLDivElement | undefined;
        let mouseDownInsideDialog = false;
        // Focus has to go back where it came from, or dismissing the dialog drops the user at the
        // top of the page instead of the control they opened it from.
        const previouslyFocused = document.activeElement as HTMLElement | null;

        function submit() {
          state().resolve({ ...values() });
          setDialogState(null);
        }

        function submitWithValue(key: string, value: string) {
          const v = { ...values() };
          v[key] = value;
          state().resolve(v);
          setDialogState(null);
        }

        function cancel() {
          state().resolve(null);
          setDialogState(null);
        }

        function handleKeyDown(e: KeyboardEvent) {
          if (e.key === "Escape") { e.preventDefault(); cancel(); return; }
          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); return; }

          // Without this the dialog is not modal for keyboard users: Tab walks straight out of it
          // into the page behind the overlay.
          if (e.key !== "Tab" || !dialogRef) return;
          const focusable = [...dialogRef.querySelectorAll<HTMLElement>(
            'input, select, button, [href], [tabindex]:not([tabindex="-1"])')]
            .filter(el => !el.hasAttribute("disabled"));
          if (focusable.length === 0) return;

          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }

        function handleOverlayMouseDown(e: MouseEvent) {
          if (e.target === e.currentTarget) mouseDownInsideDialog = false;
        }

        function handleDialogMouseDown() {
          mouseDownInsideDialog = true;
        }

        function handleOverlayMouseUp(e: MouseEvent) {
          // Cancel only if both mousedown and mouseup landed on the overlay (drag-select through dialog content should not dismiss).
          if (e.target === e.currentTarget && !mouseDownInsideDialog) cancel();
          mouseDownInsideDialog = false;
        }

        onMount(() => {
          document.addEventListener("keydown", handleKeyDown);
          setTimeout(() => (firstInput ?? dialogRef?.querySelector("button"))?.focus(), 0);
        });
        onCleanup(() => {
          document.removeEventListener("keydown", handleKeyDown);
          // Deferred: restoring focus synchronously, still inside the keydown handler that closed
          // this dialog, re-focuses the trigger element before the browser finishes that same
          // keystroke - Enter's default activation then fires again and reopens the dialog.
          setTimeout(() => previouslyFocused?.focus?.(), 0);
        });

        return (
          <Portal mount={document.body}>
            <div
              class={`${styles.overlay} ${styles.hostOverlay}`}
              onMouseDown={handleOverlayMouseDown}
              onMouseUp={handleOverlayMouseUp}
            >
              <div
                ref={dialogRef}
                class={styles.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="dialog-title"
                onMouseDown={handleDialogMouseDown}
              >
                <div class={styles.title} id="dialog-title">{state().title}</div>
                <Show when={state().description}>
                  <div class={styles.description} innerHTML={state().description} />
                </Show>
                <Show when={state().fields.length > 0}>
                  <div class={styles.fields}>
                    <For each={state().fields}>
                      {(f, i) => (
                        <div class={styles.field}>
                          <label class={styles.label}>{f.label}</label>
                          <Show when={f.type === "select"} fallback={
                            <input
                              ref={(el) => { if (i() === 0) firstInput = el; }}
                              class={styles.input}
                              type={f.quantityParse ? "text" : (f.type ?? "text")}
                              value={values()[f.key] ?? ""}
                              placeholder={f.placeholder}
                              min={f.min}
                              max={f.max}
                              onInput={(e) => setValues({ ...values(), [f.key]: e.currentTarget.value })}
                            />
                          }>
                            <select
                              ref={(el) => { if (i() === 0) firstInput = el; }}
                              class={styles.input}
                              value={values()[f.key] ?? ""}
                              onChange={(e) => setValues({ ...values(), [f.key]: e.currentTarget.value })}
                            >
                              <Show when={f.placeholder}>
                                <option value="">{f.placeholder}</option>
                              </Show>
                              <For each={f.options ?? []}>
                                {(o) => <option value={o.value}>{o.label}</option>}
                              </For>
                            </select>
                          </Show>
                          <Show when={f.hint}>
                            <span class={styles.hint}>{f.hint}</span>
                          </Show>
                          <Show when={f.quantityParse && values()[f.key]}>
                            {(() => {
                              const parsed = () => parseQuantityExpression(values()[f.key] ?? "");
                              return (
                                <Show when={parsed() !== null} fallback={<span class={styles.qtyInvalid}>?</span>}>
                                  <span class={styles.qtyPreview}>
                                    = {formatInteger(parsed()!)}
                                    <Show when={quantityBreakdown(parsed()!)}>
                                      {(breakdown) => <> ({breakdown()})</>}
                                    </Show>
                                  </span>
                                </Show>
                              );
                            })()}
                          </Show>
                          <Show when={f.presets && f.presets.length > 0}>
                            <div class={styles.presets}>
                              <For each={f.presets}>
                                {(p) => (
                                  <button
                                    class={styles.presetBtn}
                                    onClick={() => submitWithValue(f.key, p.value)}
                                  >
                                    {p.label}
                                  </button>
                                )}
                              </For>
                            </div>
                          </Show>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>
                <div class={styles.actions}>
                  <button class={styles.cancelBtn} onClick={cancel}>
                    {state().cancelLabel ?? t("CANCEL")}
                  </button>
                  <button
                    class={`${styles.confirmBtn} ${state().danger ? styles.dangerBtn : ""}`}
                    onClick={submit}
                  >
                    {state().confirmLabel ?? (state().danger ? t("DELETE") : t("CONFIRM_OK"))}
                  </button>
                </div>
              </div>
            </div>
          </Portal>
        );
      }}
    </Show>
  );
}
